/**
 * AssistedTravelDemo UI Components
 * 
 * Shared UI components for the Assisted Travel Demo including disruption cards,
 * value proposition banners, and outcome summary cards.
 */

import React from 'react';
import { Badge } from '../../ui/badge';
import { 
  AlertTriangle,
  MapPin,
  ThumbsUp,
  ShieldCheck,
  Scale
} from 'lucide-react';

// Disruption event card - compact
export const DisruptionCard = ({ disruption }) => {
  const severityColors = {
    low: 'bg-slate-500',
    medium: 'bg-amber-500',
    high: 'bg-red-500',
    critical: 'bg-red-600'
  };
  
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-white text-sm font-medium">{disruption.type.replace(/_/g, ' ')}</span>
        </div>
        <Badge className={severityColors[disruption.severity]}>
          {disruption.severity.toUpperCase()}
        </Badge>
      </div>
      <p className="text-slate-400 text-sm">{disruption.description}</p>
      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
        <MapPin className="w-3 h-3" />
        <span>{disruption.location}</span>
        {disruption.affects_accessibility && (
          <Badge variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30 text-purple-400 ml-2">
            Affects Accessibility
          </Badge>
        )}
      </div>
    </div>
  );
};

// Value proposition banners shown before running demo
export const ValuePropositionBanner = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 text-center">
      <ThumbsUp className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
      <h3 className="font-semibold text-white text-lg mb-2">Better Decision</h3>
      <p className="text-slate-400 text-sm">Advice that accounts for specific accessibility needs, not generic suggestions</p>
    </div>
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 text-center">
      <ShieldCheck className="w-10 h-10 text-amber-400 mx-auto mb-3" />
      <h3 className="font-semibold text-white text-lg mb-2">Reduced Risk</h3>
      <p className="text-slate-400 text-sm">Unsafe unverified advice excluded automatically - no manual review needed</p>
    </div>
    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-5 text-center">
      <Scale className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
      <h3 className="font-semibold text-white text-lg mb-2">Attributable Evidence</h3>
      <p className="text-slate-400 text-sm">Complete audit trail with cryptographic proof if ever challenged</p>
    </div>
  </div>
);

// Business outcomes summary cards shown after decision
export const OutcomeCards = ({ decision }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <ThumbsUp className="w-8 h-8 text-emerald-400" />
        <h3 className="font-semibold text-white text-lg">Better Decision</h3>
      </div>
      <p className="text-slate-300 text-sm">
        {decision.passenger_profile.name} receives a recommendation that accounts for her 
        <strong className="text-emerald-400"> lift access requirement</strong> and 
        <strong className="text-emerald-400"> critical appointment time</strong> - 
        not generic advice that ignores her needs.
      </p>
    </div>
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <ShieldCheck className="w-8 h-8 text-amber-400" />
        <h3 className="font-semibold text-white text-lg">Reduced Risk</h3>
      </div>
      <p className="text-slate-300 text-sm">
        The unsafe "TravelHelper App" advice was 
        <strong className="text-amber-400"> excluded automatically</strong> - 
        no staff member had to manually review and reject it. 
        The system enforces safety as a constraint.
      </p>
    </div>
    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <Scale className="w-8 h-8 text-cyan-400" />
        <h3 className="font-semibold text-white text-lg">Attributable Evidence</h3>
      </div>
      <p className="text-slate-300 text-sm">
        If this decision is ever challenged, there's a 
        <strong className="text-cyan-400"> complete audit trail</strong>: 
        which advice was considered, what was excluded and why, 
        and a cryptographic receipt proving the decision was made with this evidence.
      </p>
    </div>
  </div>
);
